import ConnectWalletButton from 'components/ConnectWalletButton'
import { ReactNode, useEffect, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import QrCode from 'views/LockTools/components/QrCode'
import { telegramShareUrl, xShareUrl } from 'views/LockTools/logic/certificate'
import { ContractContext } from 'views/TradingCompetitionV3/contract'
import { useV3Tx } from 'views/TradingCompetitionV3/components/useV3Tx'
import { useProfiles } from 'views/TradingCompetitionV3/identity/useProfiles'
import { competitionV4Abi } from 'views/TradingCompetitionV4/abi'
import { V4_BINDING } from 'views/TradingCompetitionV4/binding'
import { V4_ADDRESS } from 'views/TradingCompetitionV4/config'
import { GLOW, short } from '../avatar'
import { TxState } from '../chainTx'
import { ActionBtn, Hint, Icon, Note, SectionTitle, Surface } from '../Identity'
import { TxStatus } from '../onchainUi'
import { referralPath, referralUrl } from '../referral'
import { useReferralInfo } from '../useReferral'
import {
  CUR_INDEX,
  CURS,
  Cur,
  FeePair,
  NONE,
  SYMBOL,
  earnCardPath,
  earnedText,
  example,
  fmtAmount,
  fmtExact,
  fmtUsd,
  kitTexts,
  leadSentence,
  pct,
  progressFor,
  progressText,
  tierRows,
  usdOf,
} from './logic'
import { useEarnBoard, useEarnChain, useEarnConfig, useFeePrices } from './useEarn'

/* ───────────────────────── surfaces ───────────────────────── */

const ICE = '#7fd4e8'
const VIOLET = '#9a7bff'
const GOLD = '#ffc24a'
const LOGO: Record<Cur, string> = { bnb: '/images/chains/56.png', cbon: '/cbon-logo.png' }

const Wrap = styled.div`
  display: grid;
  gap: 14px;
  min-width: 0;
  scroll-margin-top: 120px;
  ${({ theme }) => theme.mediaQueries.md} {
    gap: 16px;
  }
`
const Hero = styled(Surface)`
  position: relative;
  overflow: hidden;
  isolation: isolate;
  background: radial-gradient(90% 120% at 100% 0%, rgba(154, 123, 255, 0.28), transparent 60%), radial-gradient(80% 90% at 0% 100%, rgba(127, 212, 232, 0.18), transparent 60%),
    ${({ theme }) => theme.colors.backgroundAlt};
  border-color: rgba(127, 212, 232, 0.26);
  box-shadow: 0 24px 60px -28px rgba(33, 0, 255, 0.55), 0 16px 40px -24px rgba(0, 0, 0, 0.7);
`
const HeroTop = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: start;
`
const float = keyframes`from { transform: translateY(0) rotate(-6deg); } to { transform: translateY(-6px) rotate(-3deg); }`
const Bag = styled.img`
  width: 72px;
  height: 72px;
  filter: drop-shadow(0 14px 22px rgba(0, 0, 0, 0.5)) drop-shadow(0 0 22px rgba(255, 194, 74, 0.45));
  animation: ${float} 3.2s cubic-bezier(0.45, 0, 0.55, 1) infinite alternate;
  ${({ theme }) => theme.mediaQueries.md} {
    width: 104px;
    height: 104px;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`
const Title = styled.h2`
  margin: 0;
  font-size: clamp(24px, 6.4vw, 30px);
  line-height: 1.1;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.text};
  text-wrap: balance;
  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 34px;
  }
`
const Lead = styled.p`
  margin: 10px 0 0;
  max-width: 62ch;
  font-size: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text};
  min-height: 48px;
  strong {
    color: ${GOLD};
  }
`
const LinkGrid = styled.div`
  display: grid;
  gap: 14px;
  margin-top: 16px;
  ${({ theme }) => theme.mediaQueries.sm} {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
  }
`
const Label = styled.span`
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textSubtle};
  margin-bottom: 6px;
`
const LinkBox = styled.code`
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 10px 14px;
  border-radius: 14px;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 15px;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.text};
  background: rgba(4, 7, 20, 0.55);
  border: 1px solid rgba(127, 212, 232, 0.35);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04), 0 0 24px -12px ${GLOW};
`
const Twins = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 8px;
  /* a link and a button side by side read as twins: same face, size and weight */
  > * {
    width: 100%;
    font-family: inherit;
    font-size: 15px;
    font-weight: 700;
  }
`
const QrSide = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  ${({ theme }) => theme.mediaQueries.sm} {
    flex-direction: column;
    gap: 6px;
  }
  p {
    margin: 0;
    font-size: 13px;
    line-height: 1.45;
    color: ${({ theme }) => theme.colors.textSubtle};
    max-width: 22ch;
    ${({ theme }) => theme.mediaQueries.sm} {
      text-align: center;
      max-width: 16ch;
    }
  }
`
const Callout = styled.div`
  display: grid;
  gap: 10px;
  margin-top: 14px;
  padding: 14px;
  border-radius: 16px;
  background: rgba(255, 178, 55, 0.1);
  border: 1px solid rgba(255, 194, 74, 0.45);
  color: #ffe2a6;
  font-size: 14px;
  line-height: 1.5;
  strong {
    color: #fff1cf;
  }
  ${({ theme }) => theme.mediaQueries.sm} {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
  }
`

/* tiles: BNB and CBON are twins (same rows, same heights) */
const TileGrid = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 14px;
  ${({ theme }) => theme.mediaQueries.sm} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`
const Tile = styled.div<{ $live: boolean }>`
  display: grid;
  grid-template-rows: auto auto auto 1fr auto;
  gap: 10px;
  min-width: 0;
  padding: 16px;
  border-radius: 20px;
  background: linear-gradient(180deg, rgba(20, 30, 66, 0.75), rgba(8, 12, 30, 0.6));
  border: 1px solid rgba(255, 255, 255, 0.07);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05), 0 14px 30px -20px rgba(0, 0, 0, 0.8);
  ${({ $live }) =>
    $live &&
    css`
      border-color: rgba(255, 194, 74, 0.5);
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 16px 36px -18px rgba(255, 194, 74, 0.45);
    `}
`
const Coin = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
  img {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.45);
  }
`
const Big = styled.div`
  font-size: clamp(28px, 8vw, 34px);
  line-height: 1.05;
  font-weight: 800;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text};
  min-height: 36px;
  overflow-wrap: anywhere;
`
const Sub = styled.div`
  font-size: 14px;
  line-height: 20px;
  min-height: 20px;
  color: ${({ theme }) => theme.colors.textSubtle};
  font-variant-numeric: tabular-nums;
  strong {
    color: ${({ theme }) => theme.colors.text};
  }
`
/** two lines reserved: the line under the bar never makes the twin tiles jump when the figures arrive */
const Two = styled(Sub)`
  min-height: 40px;
`
const Track = styled.div<{ $pct: number }>`
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, ${ICE}, ${VIOLET});
    box-shadow: 0 0 14px rgba(127, 212, 232, 0.7);
    /* the fill slides in from the left (transform only, no layout work) */
    transform: translateX(${({ $pct }) => Math.max(3, Math.min(100, $pct)) - 100}%);
    transition: transform 600ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  @media (prefers-reduced-motion: reduce) {
    &::after {
      transition: none;
    }
  }
`
const Confirm = styled.div`
  display: grid;
  gap: 10px;
  padding: 12px;
  border-radius: 14px;
  background: rgba(127, 212, 232, 0.08);
  border: 1px solid rgba(127, 212, 232, 0.3);
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text};
  strong {
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
  }
`

/* tier ladder */
const Ladder = styled.ol`
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
  display: grid;
  gap: 8px;
`
const Rung = styled.li<{ $on: boolean }>`
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  gap: 6px 12px;
  align-items: center;
  padding: 12px;
  border-radius: 16px;
  background: rgba(8, 12, 30, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.06);
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: 92px minmax(0, 1fr) minmax(0, 1.1fr);
    padding: 12px 16px;
  }
  ${({ $on }) =>
    $on &&
    css`
      background: linear-gradient(90deg, rgba(127, 212, 232, 0.14), rgba(154, 123, 255, 0.1));
      border-color: rgba(127, 212, 232, 0.45);
      box-shadow: 0 12px 30px -18px rgba(127, 212, 232, 0.6);
    `}
  .rate {
    grid-row: span 2;
    display: grid;
    place-items: center;
    height: 56px;
    border-radius: 14px;
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    color: ${({ $on }) => ($on ? '#0b1220' : '#e8f4ff')};
    background: ${({ $on }) => ($on ? `linear-gradient(135deg, #8fe3f5, ${ICE} 40%, ${VIOLET})` : 'rgba(127, 212, 232, 0.1)')};
    box-shadow: ${({ $on }) => ($on ? '0 10px 24px -8px rgba(127, 212, 232, 0.7), inset 0 1px 0 rgba(255,255,255,0.4)' : 'inset 0 0 0 1px rgba(127, 212, 232, 0.22)')};
    ${({ theme }) => theme.mediaQueries.md} {
      grid-row: auto;
    }
  }
  .range {
    font-size: 16px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.text};
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 8px;
  }
  .ex {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 14px;
    > span {
      white-space: nowrap;
    }
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme }) => theme.colors.textSubtle};
    font-variant-numeric: tabular-nums;
    strong {
      color: ${({ theme }) => theme.colors.text};
    }
  }
`
const Here = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  color: #0b1220;
  background: ${GOLD};
  box-shadow: 0 4px 14px -4px rgba(255, 194, 74, 0.7);
`
const Notes = styled.ul`
  margin: 12px 0 0;
  padding: 0 0 0 18px;
  display: grid;
  gap: 4px;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSubtle};
`

/* lists: history and board */
const List = styled.ol`
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: grid;
  gap: 6px;
  min-height: 168px;
`
const Item = styled.li`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 6px 12px;
  border-radius: 14px;
  background: rgba(8, 12, 30, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.05);
  font-variant-numeric: tabular-nums;
  .who {
    min-width: 0;
    display: grid;
    gap: 2px;
  }
  .who a,
  .who strong {
    font-size: 15px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.text};
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .who a:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  .who span {
    font-size: 13px;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
  .amt {
    display: grid;
    justify-items: end;
    font-size: 15px;
    font-weight: 800;
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.text};
    text-align: right;
  }
  .rank {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border-radius: 12px;
    font-size: 15px;
    font-weight: 800;
    color: ${({ theme }) => theme.colors.textSubtle};
    background: rgba(255, 255, 255, 0.05);
  }
  .rank img {
    width: 36px;
    height: 36px;
    filter: drop-shadow(0 6px 10px rgba(0, 0, 0, 0.45));
  }
  &[data-me='true'] {
    border-color: rgba(255, 194, 74, 0.5);
  }
`
const shimmer = keyframes`from { background-position: -200px 0; } to { background-position: 200px 0; }`
const Ghost = styled.li`
  min-height: 52px;
  border-radius: 14px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.03)) 0 0 / 400px 100%;
  animation: ${shimmer} 1.4s linear infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`
const Empty = styled.li`
  display: grid;
  place-items: center;
  text-align: center;
  min-height: 120px;
  padding: 16px;
  border-radius: 16px;
  border: 1px dashed rgba(127, 212, 232, 0.25);
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSubtle};
`

/* shiller kit */
const KitGrid = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 14px;
  ${({ theme }) => theme.mediaQueries.lg} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`
const KitCard = styled.div`
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 10px;
  min-width: 0;
  padding: 14px;
  border-radius: 18px;
  background: rgba(8, 12, 30, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.07);
  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 800;
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${({ theme }) => theme.colors.text};
  }
  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.55;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    color: ${({ theme }) => theme.colors.text};
    opacity: 0.92;
  }
`
const CardShot = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 14px;
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
    align-items: center;
  }
  .frame {
    position: relative;
    aspect-ratio: 1200 / 630;
    border-radius: 16px;
    overflow: hidden;
    background: #050713;
    border: 1px solid rgba(127, 212, 232, 0.2);
    box-shadow: 0 18px 40px -22px rgba(0, 0, 0, 0.9), 0 0 30px -16px ${GLOW};
  }
  .frame img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

/* ───────────────────────── small parts ───────────────────────── */

function useCopy() {
  const [done, setDone] = useState<string | null>(null)
  const copy = async (key: string, text: string) => {
    try {
      await navigator.clipboard?.writeText(text)
      setDone(key)
      setTimeout(() => setDone((d) => (d === key ? null : d)), 1500)
    } catch {
      /* no clipboard: the text stays selectable */
    }
  }
  return { done, copy }
}

const shareText = async (title: string, text: string, url?: string) => {
  try {
    if (typeof navigator !== 'undefined' && navigator.share) {
      await navigator.share({ title, text, ...(url ? { url } : {}) })
      return true
    }
  } catch {
    return true // cancelled
  }
  return false
}

const day = (t: number | null) => (t ? new Date(t * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : '')
/** "30 %" never breaks before its "%", "≈ $9" never before its amount */
const nb = (t: string) => t.replace(/ %/g, '\u00a0%').replace(/≈ /g, '≈\u00a0')

function Section({ title, hint, children, testId, id }: { title: string; hint?: ReactNode; children: ReactNode; testId?: string; id?: string }) {
  return (
    <Surface data-testid={testId} id={id}>
      {hint ? (
        <Hint label={title.toLowerCase()} text={hint}>
          <SectionTitle as="h3">{title}</SectionTitle>
        </Hint>
      ) : (
        <SectionTitle as="h3">{title}</SectionTitle>
      )}
      {children}
    </Surface>
  )
}

/* ───────────────────────── earnings tile (one per currency) ───────────────────────── */

function EarnTile({
  cur,
  wallet,
  credit,
  earned,
  count,
  tiers,
  override,
  price,
  onClaimed,
}: {
  cur: Cur
  wallet: string
  credit: bigint | undefined
  earned: bigint | undefined
  count: number | undefined
  tiers: number[] | null
  override: { set: boolean; bps: number } | null | undefined
  price: number | null
  onClaimed: () => void
}) {
  const { send, pending } = useV3Tx()
  const [confirm, setConfirm] = useState(false)
  const [tx, setTx] = useState<{ state: TxState; hash: string | null; error: string | null }>({ state: 'idle', hash: null, error: null })
  const p = count === undefined ? null : progressFor(count, tiers, override)
  const usd = credit !== undefined ? fmtUsd(usdOf(credit, price)) : null
  const has = credit !== undefined && credit > 0n
  const sym = SYMBOL[cur]
  const claim = async () => {
    setConfirm(false)
    setTx({ state: 'wallet', hash: null, error: null })
    let err: string | null = null
    const h = await send(
      `Claim ${sym} commission`,
      { address: V4_ADDRESS, abi: competitionV4Abi, functionName: 'withdrawReferralCredit', args: [CUR_INDEX[cur]] },
      {
        onHash: (hash: string) => setTx({ state: 'pending', hash, error: null }),
        onError: (m: string) => {
          err = m
        },
      },
    )
    if (h) {
      setTx({ state: 'success', hash: h, error: null })
      onClaimed()
    } else setTx((t) => ({ state: 'error', hash: t.hash, error: !err ? 'The claim did not go through.' : /reject|denied|cancel/i.test(err) ? 'Cancelled in the wallet.' : err }))
  }
  const busy = pending || tx.state === 'wallet' || tx.state === 'pending'
  return (
    <Tile $live={has} data-testid={`earn-tile-${cur}`}>
      <Coin>
        <img src={LOGO[cur]} alt="" width={26} height={26} />
        {sym}
      </Coin>
      <div>
        <Sub>Ready to claim</Sub>
        <Big data-testid={`earn-credit-${cur}`}>{credit === undefined ? '…' : fmtAmount(credit, cur)}</Big>
        <Sub>{usd && has ? nb(`≈ ${usd} now`) : '\u00a0'}</Sub>
      </div>
      <Sub>
        Earned in total <strong>{earned === undefined ? '…' : fmtAmount(earned, cur)}</strong>
      </Sub>
      <div data-testid={`earn-progress-${cur}`} style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
        <Two>
          <strong>{p ? p.count : '…'}</strong> qualifying {p?.count === 1 ? 'project' : 'projects'} · your next rate <strong>{p ? nb(pct(p.rateBps)) : '…'}</strong>
        </Two>
        <Track $pct={p ? p.inTier * 100 : 0} role="progressbar" aria-label={`${sym} tier progress`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={p ? Math.round(p.inTier * 100) : 0} />
        <Two>{p ? nb(progressText(p)) : '\u00a0'}</Two>
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {confirm && credit !== undefined ? (
          <Confirm data-testid={`earn-confirm-${cur}`}>
            <span>
              You receive exactly <strong>{fmtExact(credit)} {sym}</strong> at <strong>{short(wallet)}</strong>. Your wallet asks for one transaction
              (withdrawReferralCredit on the competition contract); its network fee is paid in BNB.
            </span>
            <Twins style={{ marginTop: 0 }}>
              <ActionBtn type="button" $primary onClick={claim} disabled={busy} data-testid={`earn-confirm-${cur}-go`}>
                Confirm in wallet
              </ActionBtn>
              <ActionBtn type="button" onClick={() => setConfirm(false)} data-testid={`earn-confirm-${cur}-cancel`}>
                Cancel
              </ActionBtn>
            </Twins>
          </Confirm>
        ) : (
          <ActionBtn type="button" $primary={has} disabled={!has || busy || !V4_ADDRESS} onClick={() => setConfirm(true)} data-testid={`earn-claim-${cur}`} style={{ width: '100%' }}>
            {credit === undefined ? 'Claim' : has ? `Claim ${fmtAmount(credit, cur)}` : 'Nothing to claim yet'}
          </ActionBtn>
        )}
        <TxStatus state={tx.state} error={tx.error} hash={tx.hash} done="Claimed to your wallet." testId={`earn-claim-${cur}-status`} />
      </div>
    </Tile>
  )
}

/* ───────────────────────── the section ───────────────────────── */

/**
 * "Earn" on the CADINU profile (Bez, 2026-10-09): the affiliate programme of the V4 competition contract for the connected wallet. The
 * link, the live tiers with dollar examples, progress, claimable credit (Claim = withdrawReferralCredit, the only transaction here), the
 * competitions the link brought, the top affiliates and a shiller kit. Without a wallet: the programme and a connect button.
 * `onRegister` opens the existing "Register as referrer" button (Rewards tab); without it the create page's button is linked.
 */
export default function EarnSection({ wallet, refId, onRegister, scrollOnMount }: { wallet?: string; refId?: string; onRegister?: () => void; scrollOnMount?: boolean }) {
  const { chain, refetch } = useEarnChain(wallet)
  const { config, loading: configLoading, failed: configFailed } = useEarnConfig()
  const { board, loading: boardLoading, failed: boardFailed } = useEarnBoard(wallet)
  const prices = useFeePrices()
  const { info } = useReferralInfo(refId || !wallet ? null : wallet)
  const id = refId ?? info?.id ?? wallet ?? ''
  const [origin, setOrigin] = useState('https://apps.cadinu.io')
  useEffect(() => setOrigin(window.location.origin), [])
  const link = id ? referralUrl(origin, id) : ''
  const { done, copy } = useCopy()
  const handles = useProfiles((board?.top ?? []).map((t) => t.address))
  const root = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!scrollOnMount) return undefined
    const t = setTimeout(() => root.current?.scrollIntoView({ block: 'start' }), 250)
    return () => clearTimeout(t)
  }, [scrollOnMount])

  if (!V4_ADDRESS)
    return (
      <Surface data-testid="earn-section" id="earn">
        <SectionTitle as="h2">Earn</SectionTitle>
        <Note style={{ marginTop: 8 }}>The competition referral programme is not available on this site yet.</Note>
      </Surface>
    )

  // no default rates: the config's (read from the contract by the API) or "—" (Bez 2026-10-09)
  const tiers = config?.tiersBps ?? null
  const ratesNote = tiers ? null : configFailed ? 'Rates unavailable right now. Try again in a minute.' : 'Rates loading…'
  const bnbFees: FeePair | null = config?.fees.bnb ?? null
  const progress = {
    bnb: chain.count.bnb === undefined ? null : progressFor(chain.count.bnb, tiers, chain.override),
    cbon: chain.count.cbon === undefined ? null : progressFor(chain.count.cbon, tiers, chain.override),
  }
  const myRate = progress.bnb?.rateBps ?? (tiers ? tiers[0] : null)
  const kit = link && config ? kitTexts(link, config.fees) : []
  const me = wallet?.toLowerCase()

  return (
    <ContractContext.Provider value={V4_BINDING}>
      <Wrap data-testid="earn-section" id="earn" ref={root}>
        <Hero $lit>
          <HeroTop>
            <div>
              <Title>Bring projects, earn {SYMBOL.bnb}</Title>
              <Lead data-testid="earn-lead">
                {bnbFees
                  ? nb(leadSentence(bnbFees, 'bnb', myRate, prices.bnb))
                  : configLoading
                  ? 'Loading the fees and rates…'
                  : 'Each project that creates a trading competition with your link pays the referral price, and you get a share of that fee.'}
              </Lead>
            </div>
            <Bag src="/images/fluent/moneybag.png" alt="" width={104} height={104} />
          </HeroTop>
          {wallet && link ? (
            <LinkGrid>
              <div style={{ minWidth: 0 }}>
                <Label>Your link</Label>
                <LinkBox data-testid="earn-link" title={link}>
                  {origin.replace(/^https?:\/\//, '')}
                  {referralPath(id)}
                </LinkBox>
                <Twins>
                  <ActionBtn type="button" $primary onClick={() => copy('link', link)} data-testid="earn-copy">
                    <Icon name={done === 'link' ? 'check' : 'copy'} size={18} />
                    {done === 'link' ? 'Copied' : 'Copy link'}
                  </ActionBtn>
                  <ActionBtn
                    type="button"
                    onClick={async () => {
                      if (!(await shareText('CADINU trading competitions', 'Create a trading competition with my link:', link))) copy('share', link)
                    }}
                    data-testid="earn-share"
                  >
                    <Icon name={done === 'share' ? 'check' : 'share'} size={18} />
                    {done === 'share' ? 'Copied' : 'Share'}
                  </ActionBtn>
                </Twins>
              </div>
              <QrSide>
                <QrCode value={link} size={120} label="QR code of your referral link" />
                <p>Let someone scan it to open your link.</p>
              </QrSide>
            </LinkGrid>
          ) : !wallet ? (
            <div style={{ marginTop: 16, display: 'grid', gap: 10, justifyItems: 'start' }}>
              <Note>Connect a wallet to get your link. Any wallet can refer; registering is free.</Note>
              <ConnectWalletButton />
            </div>
          ) : (
            <div style={{ minHeight: 120 }} aria-busy="true" />
          )}
          {wallet && chain.registered === false && (
            <Callout data-testid="earn-register-callout">
              <span>
                <strong>Register once before you share.</strong> It is free (one transaction). Until then, a project that uses your link pays the normal fee and you
                earn nothing. Creating a competition yourself registers you too.
              </span>
              {onRegister ? (
                <ActionBtn type="button" $primary onClick={onRegister} data-testid="earn-register">
                  Register as referrer
                </ActionBtn>
              ) : (
                <ActionBtn as="a" href="/trading-competition/create" $primary data-testid="earn-register">
                  Register on the create page
                </ActionBtn>
              )}
            </Callout>
          )}
        </Hero>

        {wallet && (
          <Section
            title="Your earnings"
            testId="earn-earnings"
            hint="Read from the competition contract. Each currency has its own credit and its own count: BNB fees pay you in BNB, CBON fees in CBON. Only your wallet can claim."
          >
            <TileGrid>
              {CURS.map((cur) => (
                <EarnTile
                  key={cur}
                  cur={cur}
                  wallet={wallet}
                  credit={chain.credit[cur]}
                  earned={chain.earned[cur]}
                  count={chain.count[cur]}
                  tiers={tiers}
                  override={chain.override}
                  price={prices[cur]}
                  onClaimed={() => refetch()}
                />
              ))}
            </TileGrid>
          </Section>
        )}

        <Section title="Your share grows with every project" testId="earn-tiers">
          <Note style={{ marginTop: 6 }}>Per project: what you get when it pays in BNB, or in CBON.</Note>
          <Ladder>
            {tierRows(tiers).map((r) => {
              const here = { bnb: progress.bnb?.tier === r.index && !progress.bnb.custom, cbon: progress.cbon?.tier === r.index && !progress.cbon.custom }
              const b = bnbFees && r.bps !== null ? example(bnbFees, 'bnb', r.bps, prices.bnb) : null
              const c = config?.fees.cbon?.enabled && r.bps !== null ? example(config.fees.cbon, 'cbon', r.bps, prices.cbon) : null
              return (
                <Rung key={r.index} $on={here.bnb} data-testid="earn-tier" data-current={here.bnb ? 'true' : 'false'}>
                  <span className="rate">{nb(pct(r.bps))}</span>
                  <span className="range">
                    {r.range[0].toUpperCase() + r.range.slice(1)}
                    {here.bnb && here.cbon ? <Here>You are here</Here> : here.bnb ? <Here>Your BNB rate</Here> : here.cbon ? <Here>Your CBON rate</Here> : null}
                  </span>
                  <span className="ex">
                    {!b && !c && <span data-testid="earn-tier-pending">{NONE}</span>}
                    {b && (
                      <span>
                        <strong>{b.share}</strong>
                        {b.usd ? nb(` ≈ ${b.usd}`) : ''}
                      </span>
                    )}
                    {c && (
                      <span>
                        <strong>{c.share}</strong>
                        {c.usd ? nb(` ≈ ${c.usd}`) : ''}
                      </span>
                    )}
                  </span>
                </Rung>
              )
            })}
          </Ladder>
          {ratesNote && (
            <Note style={{ marginTop: 10 }} role="status" data-testid="earn-rates-note">
              {ratesNote}
            </Note>
          )}
          <Notes>
            <li>
              The share is of the fee the project actually pays with your link ({bnbFees ? `${fmtAmount(bnbFees.withRef, 'bnb')} instead of ${fmtAmount(bnbFees.list, 'bnb')}` : 'the referral price'}
              {config?.fees.cbon?.enabled ? `, or ${fmtAmount(config.fees.cbon.withRef, 'cbon')} instead of ${fmtAmount(config.fees.cbon.list, 'cbon')}` : ''}). A promo or verified-token discount lowers that fee, and your share with it.
            </li>
            <li>BNB and CBON creations are counted separately. A project moves you up only when its prize is a listed reward token at or above the listed minimum; every paid creation with your link pays your share either way.</li>
            {chain.override?.set && <li>A fixed rate of {pct(chain.override.bps)} is set for your wallet; the tiers do not apply to it.</li>}
            <li>Dollar figures use today&apos;s price and change with it.</li>
          </Notes>
        </Section>

        {wallet && (
          <Section title="Projects you brought" testId="earn-history" hint="Competitions created with your link that paid you a share, newest first.">
            <List aria-busy={boardLoading}>
              {boardLoading ? (
                [0, 1, 2].map((i) => <Ghost key={i} aria-hidden />)
              ) : boardFailed ? (
                <Empty>The list could not be loaded. Your earnings above are read from the contract and are current.</Empty>
              ) : !board?.history?.length ? (
                <Empty>No competition has been created with your link yet. Send it to a project with the kit below.</Empty>
              ) : (
                board.history.map((h) => (
                  <Item key={h.id}>
                    <span className="rank">
                      <Icon name="trophy" size={18} />
                    </span>
                    <span className="who">
                      <a href={`/trading-competition/${h.id}`}>Competition #{h.id}</a>
                      <span>
                        {h.startTime ? `Starts ${day(h.startTime)} · ` : ''}paid {fmtAmount(BigInt(h.feePaidWei), h.currency)}
                        {h.tx ? (
                          <>
                            {' · '}
                            <a href={`https://bscscan.com/tx/${h.tx}`} target="_blank" rel="noreferrer" style={{ fontSize: 13, fontWeight: 600, display: 'inline' }}>
                              tx
                            </a>
                          </>
                        ) : null}
                      </span>
                    </span>
                    <span className="amt">+{fmtAmount(BigInt(h.commissionWei), h.currency)}</span>
                  </Item>
                ))
              )}
            </List>
          </Section>
        )}

        <Section title="Top affiliates" testId="earn-board" hint="Every wallet whose link brought a paid competition, ranked by projects brought, then by what it earned. From the contract's own records; nothing private.">
          <List aria-busy={boardLoading}>
            {boardLoading ? (
              [0, 1, 2].map((i) => <Ghost key={i} aria-hidden />)
            ) : boardFailed ? (
              <Empty>The board could not be loaded. Try again in a minute.</Empty>
            ) : !board?.top.length ? (
              <Empty>No affiliate has brought a paid competition yet. The first one leads this board.</Empty>
            ) : (
              board.top.map((t, i) => {
                const prof = handles[t.address]
                const name = prof?.state === 'on' && prof.name ? `@${prof.name}` : short(t.address)
                return (
                  <Item key={t.address} data-me={t.address === me ? 'true' : 'false'}>
                    <span className="rank">{i < 3 ? <img src={`/images/fluent/medal${i + 1}.png`} alt={`#${i + 1}`} width={36} height={36} /> : i + 1}</span>
                    <span className="who">
                      <a href={`/u/${t.address}`}>{name}</a>
                      <span>
                        {t.count} {t.count === 1 ? 'project' : 'projects'}
                        {t.address === me ? ' · you' : ''}
                      </span>
                    </span>
                    <span className="amt" aria-label={earnedText(t.bnbWei, t.cbonWei)}>
                      {earnedText(t.bnbWei, t.cbonWei)
                        .split(' · ')
                        .map((x) => (
                          <span key={x}>{x}</span>
                        ))}
                    </span>
                  </Item>
                )
              })
            )}
          </List>
          {board?.capped && <Note style={{ marginTop: 8 }}>Counted over the newest {board.scanned} competitions.</Note>}
        </Section>

        {wallet && link && (
          <Section title="Shiller kit" testId="earn-kit" hint="Texts that say only what the contract does: no promise of prices, results or safety. Edit them as you like, keep them true.">
            <KitGrid>
              {kit.map((k) => (
                <KitCard key={k.id} data-testid={`earn-kit-${k.id}`}>
                  <h3>
                    <Icon name={k.id === 'x' ? 'megaphone' : k.id === 'telegram' ? 'send' : 'users'} size={18} />
                    {k.label}
                  </h3>
                  <p data-testid="earn-kit-text">{k.text}</p>
                  <Twins style={{ marginTop: 0 }}>
                    <ActionBtn type="button" $primary onClick={() => copy(k.id, k.text)} data-testid={`earn-kit-copy-${k.id}`}>
                      <Icon name={done === k.id ? 'check' : 'copy'} size={18} />
                      {done === k.id ? 'Copied' : 'Copy'}
                    </ActionBtn>
                    {k.id === 'x' ? (
                      <ActionBtn as="a" href={xShareUrl(k.text.replace(link, '').trim(), link)} target="_blank" rel="noreferrer">
                        Post on X
                      </ActionBtn>
                    ) : k.id === 'telegram' ? (
                      <ActionBtn as="a" href={telegramShareUrl(k.text.replace(link, '').trim(), link)} target="_blank" rel="noreferrer">
                        Telegram
                      </ActionBtn>
                    ) : (
                      <ActionBtn
                        type="button"
                        onClick={async () => {
                          if (!(await shareText('CADINU trading competition', k.text))) copy(k.id, k.text)
                        }}
                      >
                        Share
                      </ActionBtn>
                    )}
                  </Twins>
                </KitCard>
              ))}
            </KitGrid>
            <CardShot>
              <div className="frame">
                <img src={earnCardPath(id)} alt="Your share card: launch a trading competition, with your link and its QR code" loading="lazy" width={1200} height={630} />
              </div>
              <div style={{ display: 'grid', gap: 10, alignContent: 'start' }}>
                <strong style={{ fontSize: 16 }}>Share card</strong>
                <Note>A picture for posts and chats, with your link and its QR code. No fee figure is printed on it, so it stays true when fees change.</Note>
                <Twins style={{ marginTop: 0 }}>
                  <ActionBtn as="a" $primary href={`${earnCardPath(id)}?download=1`} download={`cadinu-earn-${id}.png`} data-testid="earn-card-download">
                    Download
                  </ActionBtn>
                  <ActionBtn type="button" onClick={() => copy('card', `${origin}${earnCardPath(id)}`)}>
                    <Icon name={done === 'card' ? 'check' : 'link'} size={18} />
                    {done === 'card' ? 'Copied' : 'Image link'}
                  </ActionBtn>
                </Twins>
              </div>
            </CardShot>
          </Section>
        )}
      </Wrap>
    </ContractContext.Provider>
  )
}
